import React from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  GroupsOutlined as GroupsIcon,
  MapOutlined as MapIcon,
  CalendarMonth as CalendarIcon,
  Flight as FlightIcon,
  AttachMoney as MoneyIcon,
  Share as ShareIcon,
  ExpandMore as ExpandMoreIcon,
  Route as RouteIcon,
  ForwardToInbox as ImportIcon,
  DirectionsCar as RoadTripIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '../components/SEOHead';
import { getFAQSchema } from '../utils/seoSchemas';
import DocumentMarketingHero from '../components/DocumentMarketingHero';
import { DOCUMENT, PAGE_CONTAINER_MAX_WIDTH, documentPageCanvasSx, documentPageContainerSx } from '../constants/documentTheme';
import { partnerOxbloodBtnSx, partnerOutlineBtnSx } from '../components/PartnerListChrome';

const TripPlanner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const features = [
    {
      icon: <CalendarIcon sx={{ fontSize: 32, color: '#2e7d6c' }} />,
      title: 'Visual Itinerary Builder',
      description: 'Create day-by-day travel itineraries with drag-and-drop simplicity. Add activities, restaurants, and attractions.',
    },
    {
      icon: <GroupsIcon sx={{ fontSize: 32, color: '#2e7d6c' }} />,
      title: 'Group Trip Planning',
      description: 'Invite friends and family to collaborate on trip plans. Vote on activities and make decisions together.',
    },
    {
      icon: <FlightIcon sx={{ fontSize: 32, color: '#2e7d6c' }} />,
      title: 'Flight & Hotel Search',
      description: 'Search and compare flights and hotels directly in the app. Find the best deals for your trip.',
    },
    {
      icon: <MoneyIcon sx={{ fontSize: 32, color: '#2e7d6c' }} />,
      title: 'Expense Tracking',
      description: 'Track trip expenses and split costs with your travel group. Never lose track of who owes what.',
    },
    {
      icon: <MapIcon sx={{ fontSize: 32, color: '#2e7d6c' }} />,
      title: 'Interactive Maps',
      description: 'See all your planned activities on an interactive map. Optimize your route and save time.',
    },
    {
      icon: <ShareIcon sx={{ fontSize: 32, color: '#2e7d6c' }} />,
      title: 'Share & Export',
      description: 'Share your itinerary with others or export it for offline use. Access your plans anywhere.',
    },
    {
      icon: <RouteIcon sx={{ fontSize: 32, color: '#2e7d6c' }} />,
      title: 'Route Optimization',
      description: 'Automatically reorder your daily stops to minimize travel time using Google Maps distance calculations.',
    },
    {
      icon: <RoadTripIcon sx={{ fontSize: 32, color: '#2e7d6c' }} />,
      title: 'Road Trip Planner',
      description: 'Plan multi-stop road trips with optimized routes. Discover restaurants, hotels, gas stations, and attractions along the way.',
    },
    {
      icon: <ImportIcon sx={{ fontSize: 32, color: '#2e7d6c' }} />,
      title: 'Email Import',
      description: 'Paste confirmation emails to auto-detect flights, hotels, car rentals, restaurants, and events into your itinerary.',
    },
  ];

  const steps = [
    {
      number: '1',
      title: 'Create Your Trip',
      description: 'Start by entering your destination and travel dates. Our trip planner will help you organize everything.',
    },
    {
      number: '2',
      title: 'Add Activities & Bookings',
      description: 'Search for flights, hotels, and attractions — or import reservations from confirmation emails.',
    },
    {
      number: '3',
      title: 'Invite Your Group',
      description: 'Planning a group trip? Invite friends and family to view and edit the itinerary together.',
    },
    {
      number: '4',
      title: 'Book, Go & Share',
      description: 'Finalize bookings, optimize your route, and enjoy your trip!',
    },
  ];

  const faqs = [
    {
      question: 'Is the trip planner free to use?',
      answer: 'Yes! Déjà Vu Nomad offers a free trip planner with all essential features. Create unlimited trips, invite group members, and plan your perfect vacation at no cost.',
    },
    {
      question: 'Can I plan group trips with multiple people?',
      answer: 'Absolutely! Our trip planner is designed for group travel. Invite friends, family, or travel companions to collaborate on the itinerary. Everyone can add suggestions, vote on activities, and stay in sync.',
    },
    {
      question: 'Does the trip planner work offline?',
      answer: 'Yes, you can access your trip itinerary offline. Export your plans before you travel, and access them anytime without an internet connection.',
    },
    {
      question: 'Can I book flights and hotels through the trip planner?',
      answer: 'Yes! Search and compare flights and hotels directly in our app. We partner with major travel providers to offer competitive prices and seamless booking.',
    },
    {
      question: 'How do I share my trip itinerary with others?',
      answer: 'Share your trip with a simple invite link. Recipients can view the full itinerary, and if you grant permission, they can also edit and add their own suggestions.',
    },
    {
      question: 'How does route optimization work?',
      answer: 'Our route optimizer uses Google Maps to calculate travel times between all your daily stops and reorders them to minimize total driving time. It works per day and shows you exactly how many minutes you\'ll save.',
    },
    {
      question: 'How does the Road Trip Planner work?',
      answer: 'Enter your starting point, destination, and any stops along the way. Our road trip planner calculates the optimal route and helps you discover restaurants, hotels, gas stations, and attractions near your route. You can customize stops, adjust the route, and plan the perfect road trip.',
    },
    {
      question: 'Can I import reservations from my email?',
      answer: 'Yes! Paste your confirmation email from airlines, hotels, car rental agencies, restaurants, or event services. Our system automatically detects the reservation details and adds them to your itinerary.',
    },
    {
      question: 'Is my trip data secure?',
      answer: 'Yes, we take privacy seriously. Your trip data is encrypted and stored securely. You control who can see and edit your trips.',
    },
  ];

  const faqSchema = getFAQSchema(faqs);

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Déjà Vu Nomad Trip Planner",
      "applicationCategory": "TravelApplication",
      "operatingSystem": "Web, iOS, Android",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "ratingCount": "1250"
      },
      "description": "Free trip planner app for organizing vacations, group travel, and travel itineraries"
    },
    faqSchema
  ];

  return (
    <Box sx={documentPageCanvasSx}>
      <SEOHead
        title="Free Trip Planner | Plan Your Vacation Online - Déjà Vu Nomad"
        description="Plan your trip for free with our online trip planner. Create travel itineraries, organize group vacations, book flights & hotels, and share plans with friends. The best trip planning app for 2026."
        keywords="trip planner, trip planning, travel planner, vacation planner, itinerary planner, travel itinerary, trip organizer, group trip planner, free trip planner, online trip planner, travel planning app, vacation planning"
        canonical="https://dejavunomad.com/trip-planner"
        structuredData={structuredData}
      />

      <DocumentMarketingHero
        eyebrow={t('nav.planATrip')}
        title="The Easiest Way to Plan Your Trip"
        subtitle="Create beautiful travel itineraries, collaborate with your group, and book everything in one place. Our free trip planner makes vacation planning simple and fun."
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/register')}
            sx={{
              ...partnerOxbloodBtnSx,
              bgcolor: '#C9A961',
              color: DOCUMENT.nav,
              '&:hover': { bgcolor: '#DCC183', boxShadow: 'none' },
            }}
          >
            Start Planning Free
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/deals')}
            sx={{
              ...partnerOutlineBtnSx,
              color: DOCUMENT.ivory,
              borderColor: 'rgba(246,239,228,.45)',
              '&:hover': {
                background: 'rgba(246,239,228,0.08)',
                borderColor: '#C9A961',
                boxShadow: 'none',
              },
            }}
          >
            Browse Travel Deals
          </Button>
        </Stack>
      </DocumentMarketingHero>

      {/* Features Section */}
      <Box sx={{ py: { xs: 4, md: 6 }, backgroundColor: DOCUMENT.surface, borderTop: `1px solid ${DOCUMENT.ruleHard}`, borderBottom: `1px solid ${DOCUMENT.ruleHard}` }}>
        <Container
          maxWidth={PAGE_CONTAINER_MAX_WIDTH}
          sx={documentPageContainerSx}
          data-testid="trip-planner-features"
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h2" sx={{ fontSize: { xs: '1.6rem', md: '2rem' }, fontWeight: 700, mb: 1 }}>
              Everything You Need to Plan Your Trip
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
              Our trip planner includes all the tools you need to create the perfect vacation itinerary
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                  <CardContent>
                    <Box sx={{ mb: 1.5 }}>{feature.icon}</Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, fontSize: '1rem' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box sx={{ py: { xs: 4, md: 6 } }}>
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={documentPageContainerSx}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h2" sx={{ fontSize: { xs: '1.6rem', md: '2rem' }, fontWeight: 700, mb: 1 }}>
              How to Plan Your Trip
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Get started in minutes with our simple 4-step process
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {steps.map((step, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: '#8B1538',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      mx: 'auto',
                      mb: 1.5,
                    }}
                  >
                    {step.number}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, fontSize: '1rem' }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ py: { xs: 4, md: 6 }, backgroundColor: '#2e7d6c', color: 'white' }}>
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={documentPageContainerSx}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h2" sx={{ fontSize: { xs: '1.6rem', md: '2rem' }, fontWeight: 700, mb: 2 }}>
                Why Choose Our Trip Planner?
              </Typography>
              <List>
                {[
                  '100% free to use - no hidden fees',
                  'Perfect for solo travelers and groups',
                  'Works on any device - web, mobile, tablet',
                  'Real-time collaboration with travel companions',
                  'Integrated flight and hotel search',
                  'Smart route optimization to save travel time',
                  'Road trip planner with multi-stop route planning',
                  'Import reservations from confirmation emails',
                  'Expense tracking and bill splitting',
                ].map((benefit, index) => (
                  <ListItem key={index} sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary={benefit} primaryTypographyProps={{ fontSize: '0.95rem' }} />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ fontSize: '3rem', fontWeight: 700 }}>
                  10,000+
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
                  Trips Planned
                </Typography>
                <Typography variant="h3" sx={{ fontSize: '3rem', fontWeight: 700 }}>
                  50+
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  Countries Covered
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: { xs: 4, md: 6 } }}>
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h2" sx={{ fontSize: { xs: '1.6rem', md: '2rem' }, fontWeight: 700, mb: 1 }}>
              Trip Planner FAQ
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Common questions about our trip planning tool
            </Typography>
          </Box>
          {faqs.map((faq, index) => (
            <Accordion key={index} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">{faq.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: { xs: 4, md: 5 }, backgroundColor: '#f8f9fa' }}>
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1.5, fontSize: { xs: '1.4rem', md: '1.75rem' } }}>
              Ready to Plan Your Next Adventure?
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
              Join thousands of travelers who use Déjà Vu Nomad to plan unforgettable trips
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={partnerOxbloodBtnSx}
            >
              Start Planning Your Trip Now
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default TripPlanner;
